import { useState } from 'react';
import { Button } from '@safetyhub/ui';
import Modal from '../Modal';

export interface DeleteApprovalActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  requesterName: string;
  reason: string;
  onSubmit: (action: 'APPROVE' | 'REJECT', comment?: string) => void | Promise<void>;
}

/**
 * 보고서 삭제 승인/거절 모달.
 *
 * 외부 삭제 승인 페이지(`/delete-approval/:token`) 전용.
 * shell의 renderModal에서 사용.
 */
export function DeleteApprovalActionModal({
  isOpen,
  onClose,
  requesterName,
  reason,
  onSubmit,
}: DeleteApprovalActionModalProps) {
  const [action, setAction] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [comment, setComment] = useState('');

  const handleClose = () => {
    setAction(null);
    setComment('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!action) return;
    if (action === 'REJECT' && !comment.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }
    await onSubmit(action, comment.trim() || undefined);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="보고서 삭제 승인"
      description={`${requesterName}님의 삭제 요청`}
      size="md"
    >
      <div className="space-y-5">
        <div className="bg-red-50 border border-red-200 rounded-card p-3">
          <p className="text-xs font-medium text-red-700 mb-1">삭제 사유</p>
          <p className="text-sm text-ink-800 whitespace-pre-wrap">{reason}</p>
        </div>

        <div className="flex items-start gap-2 text-sm text-red-800">
          <span className="flex-shrink-0 mt-0.5" aria-hidden>
            &#9888;
          </span>
          <p>삭제를 승인하면 보고서가 영구 삭제되며 복구할 수 없습니다.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-700 mb-2">
            처리 <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <label
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-card cursor-pointer transition-colors ${
                action === 'APPROVE'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-ink-200 hover:border-ink-300'
              }`}
            >
              <input
                type="radio"
                name="deleteAction"
                value="APPROVE"
                checked={action === 'APPROVE'}
                onChange={() => setAction('APPROVE')}
                className="sr-only"
              />
              <span className="font-medium">삭제 승인</span>
            </label>
            <label
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-card cursor-pointer transition-colors ${
                action === 'REJECT'
                  ? 'border-orange-500 bg-orange-50 text-orange-700'
                  : 'border-ink-200 hover:border-ink-300'
              }`}
            >
              <input
                type="radio"
                name="deleteAction"
                value="REJECT"
                checked={action === 'REJECT'}
                onChange={() => setAction('REJECT')}
                className="sr-only"
              />
              <span className="font-medium">거절</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            {action === 'REJECT' ? '거절 사유' : '코멘트'}
            {action === 'REJECT' && <span className="text-red-500"> *</span>}
            {action !== 'REJECT' && <span className="text-ink-400 ml-1">(선택)</span>}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-red-500 focus:border-red-500 text-sm"
            placeholder={
              action === 'REJECT'
                ? '거절 사유를 입력해주세요'
                : '요청자에게 전달할 메시지 (선택)'
            }
          />
        </div>
      </div>

      <div className="mt-6">
        <Button
          variant={action === 'REJECT' ? 'warning' : 'danger'}
          size="lg"
          onClick={handleSubmit}
          disabled={!action || (action === 'REJECT' && !comment.trim())}
          className="w-full"
        >
          {action === 'REJECT'
            ? '거절하기'
            : action === 'APPROVE'
              ? '삭제 승인하기'
              : '처리 방법을 선택하세요'}
        </Button>
      </div>
    </Modal>
  );
}
